import React, { useMemo, useId } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { mapValues } from 'lodash-es';

import Skeleton from '../../../../../components/Skeleton';
import { SmartChart } from '../../../../../components/Chart';
import { timeSpendChartConfig } from './config';
import { aiCohortsColorsMap } from '../../../configs/constants';
import { advancedPartialFilters } from '../../../configs/chart-config';

const orderedCohorts = ['high_frequency_ai_users', 'medium_frequency_ai_users'];

const cohortsNameOverrides = {
  high_frequency_ai_users: 'Hi-Frequency Users',
  medium_frequency_ai_users: 'Medium-Frequency Users',
};

const TimeSpendChart = ({
  isPending,
  series = {},
  title,
  isColumn = false,
  footerRight,
  className,
}) => {
  const chartId = useId();

  const chartConfig = useMemo(() => timeSpendChartConfig({ isColumn, series }), [series, isColumn]);

  const metaSeriesData = useMemo(
    () =>
      mapValues(series, (_, key) => ({
        color: aiCohortsColorsMap[key],
        name: cohortsNameOverrides[key],
        type: isColumn ? 'column' : 'spline',
        visible: true,
        stacking: isColumn ? 'normal' : undefined,
        borderWidth: 0,
      })),
    [series, isColumn]
  );

  return (
    <div className={cx('app-trends__chart', className)}>
      <header className="section-body-header">
        <h4 className="section-body-header__title">{title}</h4>
      </header>

      <div className="app-trends__chart-body">
        {isPending ? (
          <Skeleton height={230} />
        ) : (
          <SmartChart
            chartId={chartId}
            series={series}
            metaSeriesData={metaSeriesData}
            config={chartConfig}
            allowPartialPeriods
            filters={advancedPartialFilters}
          />
        )}
        <div className="d-flex align-items-center justify-content-between gap-16">
          <div className="ai-dashboard-legend">
            {orderedCohorts.map((cohort) => (
              <div key={cohort} className="ai-dashboard-legend__item">
                <span
                  className="ai-dashboard-legend__marker"
                  style={{ backgroundColor: aiCohortsColorsMap[cohort] }}
                />
                <span>{cohortsNameOverrides[cohort]}</span>
              </div>
            ))}
          </div>
          {footerRight}
        </div>
      </div>
    </div>
  );
};

TimeSpendChart.propTypes = {
  series: T.object,
  isColumn: T.bool,
  title: T.oneOfType([T.string, T.node]),
  isPending: T.bool.isRequired,
  footerRight: T.node,
  className: T.string,
};

export default TimeSpendChart;
